import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { ActivatedRouteSnapshot, NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { DEFAULT_PAGE_SEO } from './seo.defaults';
import { isEphemeralSeoImageUrl, resolveSeoShareImageUrl, toAbsoluteSeoUrl } from './seo-image.util';
import { buildWebPageJsonLd } from './seo-json-ld.util';
import { PageSeo } from './seo.model';

const DESC_MAX = 320;

function trimOrigin(href: string): string {
  return href.replace(/\/$/, '');
}

function truncate(text: string, max: number): string {
  const t = text.trim();
  if (t.length <= max) {
    return t;
  }
  return `${t.slice(0, max - 1).trim()}…`;
}

function toAbsoluteUrl(href: string | undefined, base: string): string {
  return toAbsoluteSeoUrl(href, base);
}

/** Deepest activated route (leaf) for `data.seo`. */
function deepestSnapshot(root: ActivatedRouteSnapshot): ActivatedRouteSnapshot {
  let r = root;
  while (r.firstChild) {
    r = r.firstChild;
  }
  return r;
}

const CANONICAL_LINK_ID = 'chainmart-link-canonical';
const JSON_LD_SCRIPT_ID = 'chainmart-json-ld';

@Injectable({ providedIn: 'root' })
export class SeoService {
  private readonly title = inject(Title);
  private readonly meta = inject(Meta);
  private readonly router = inject(Router);
  private readonly document = inject(DOCUMENT);
  private readonly platformId = inject(PLATFORM_ID);

  /**
   * Base URL for `og:url`, canonical links, and resolving relative `imageUrl` values.
   * SSR uses `publicAppOrigin` (server polyfills stub `window.location` as localhost).
   * Browser on localhost uses the address bar for `ng serve`.
   */
  private getAppPublicOrigin(): string {
    const fromConfig = environment.publicAppOrigin?.trim();

    if (!isPlatformBrowser(this.platformId)) {
      if (fromConfig) {
        return trimOrigin(fromConfig);
      }
      return `https://${environment.webSiteDomain}`;
    }

    const loc = globalThis.location;
    const isLocalhost =
      loc.hostname === 'localhost' || loc.hostname === '127.0.0.1' || loc.hostname === '[::1]';

    if (isLocalhost) {
      return trimOrigin(`${loc.protocol}//${loc.host}`);
    }

    if (fromConfig) {
      return trimOrigin(fromConfig);
    }

    if (environment.production) {
      return `https://${environment.webSiteDomain}`;
    }

    return trimOrigin(`${loc.protocol}//${loc.host}`);
  }

  /** Subscribe once (e.g. from `AppComponent`) to apply `data.seo` after each navigation. */
  listenToRouter(): void {
    this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe(() => this.applyFromRouter());
  }

  applyFromRouter(): void {
    const leaf = deepestSnapshot(this.router.routerState.snapshot.root);
    if (leaf.data['seoDynamic']) {
      return;
    }
    const routeSeo = leaf.data['seo'] as Partial<PageSeo> | undefined;
    const merged: PageSeo = { ...DEFAULT_PAGE_SEO, ...routeSeo };
    const path = this.currentPath();
    const canonical = new URL(path, this.getAppPublicOrigin()).href;
    this.applyMeta(merged, canonical);
  }

  /**
   * For dynamic pages (article, organisation, glossary) after async data loads.
   * Pass `path` when the public URL is already known. Resolvers run before `router.url`
   * commits, and reading it there canonicalises the page to `/`.
   */
  applyPageOverride(override: Partial<PageSeo>, path?: string): void {
    const leaf = deepestSnapshot(this.router.routerState.snapshot.root);
    const routeSeo = leaf.data['seo'] as Partial<PageSeo> | undefined;
    const merged: PageSeo = { ...DEFAULT_PAGE_SEO, ...routeSeo, ...override };
    const canonical = new URL(this.normalizePath(path) ?? this.currentPath(), this.getAppPublicOrigin()).href;
    this.applyMeta(merged, canonical);
  }

  private currentPath(): string {
    return this.normalizePath(this.router.url) ?? '/';
  }

  private normalizePath(path: string | undefined): string | null {
    const trimmed = path?.split('#')[0].split('?')[0].trim();
    if (!trimmed) {
      return null;
    }
    return trimmed.startsWith('/') ? trimmed : `/${trimmed}`;
  }

  private applyMeta(page: PageSeo, canonicalUrl: string): void {
    const base = this.getAppPublicOrigin();
    const desc = truncate(page.description, DESC_MAX);
    const ogDesc = truncate(page.ogDescription ?? page.description, DESC_MAX);
    const imageAbs = resolveSeoShareImageUrl(page, base);

    this.title.setTitle(page.title);

    this.meta.updateTag({ name: 'description', content: desc });

    this.meta.updateTag({ property: 'og:type', content: page.ogType ?? 'website' });
    this.meta.updateTag({ property: 'og:title', content: page.title });
    this.meta.updateTag({ property: 'og:description', content: ogDesc });
    this.meta.updateTag({ property: 'og:url', content: canonicalUrl });
    this.meta.updateTag({ property: 'og:image', content: imageAbs });

    this.meta.updateTag({ name: 'twitter:card', content: 'summary_large_image' });
    this.meta.updateTag({ name: 'twitter:title', content: page.title });
    this.meta.updateTag({ name: 'twitter:description', content: ogDesc });
    this.meta.updateTag({ name: 'twitter:image', content: imageAbs });

    this.applyArticleAndAuthorMeta(page);
    this.setCanonicalUrl(canonicalUrl);
    this.applyJsonLd(page, canonicalUrl, imageAbs);
  }

  private applyJsonLd(page: PageSeo, canonicalUrl: string, imageAbs: string): void {
    const blocks = this.resolveJsonLdBlocks(page, canonicalUrl, imageAbs);
    const head = this.document.head;

    for (const script of [...head.querySelectorAll('script[type="application/ld+json"]')]) {
      script.remove();
    }

    const script = this.document.createElement('script');
    script.id = JSON_LD_SCRIPT_ID;
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(blocks.length === 1 ? blocks[0] : blocks);
    head.appendChild(script);
  }

  private resolveJsonLdBlocks(
    page: PageSeo,
    canonicalUrl: string,
    imageAbs: string,
  ): Record<string, unknown>[] {
    const raw = page.jsonLd;
    const blocks: Record<string, unknown>[] = raw
      ? Array.isArray(raw)
        ? raw
        : [raw]
      : [
          buildWebPageJsonLd({
            name: page.title,
            description: page.description,
            url: canonicalUrl,
            datePublished: page.articlePublishedTime,
            authorName: page.articleAuthor,
          }),
        ];

    return blocks.map((block) => this.enrichJsonLdBlock(block, canonicalUrl, imageAbs));
  }

  private enrichJsonLdBlock(
    block: Record<string, unknown>,
    canonicalUrl: string,
    imageAbs: string,
  ): Record<string, unknown> {
    const type = String(block['@type'] ?? '');
    const enriched = { ...block };

    if (type === 'Organization') {
      enriched['url'] = canonicalUrl;
      if (typeof enriched['logo'] === 'string' && !isEphemeralSeoImageUrl(enriched['logo'])) {
        enriched['logo'] = toAbsoluteUrl(enriched['logo'], this.getAppPublicOrigin());
      } else {
        enriched['logo'] = imageAbs;
      }
    }

    if (type === 'Article') {
      enriched['mainEntityOfPage'] = { '@type': 'WebPage', '@id': canonicalUrl };
      if (typeof enriched['image'] === 'string' && !isEphemeralSeoImageUrl(enriched['image'])) {
        enriched['image'] = toAbsoluteUrl(enriched['image'], this.getAppPublicOrigin());
      } else {
        enriched['image'] = imageAbs;
      }
    }

    if (type === 'WebPage' || type === 'DefinedTermSet') {
      enriched['url'] = canonicalUrl;
    }

    if (type === 'DefinedTerm') {
      enriched['url'] = canonicalUrl;
      enriched['inDefinedTermSet'] = `${this.getAppPublicOrigin()}/dictionary`;
    }

    if (type === 'DefinedTermSet' && Array.isArray(enriched['hasDefinedTerm'])) {
      const origin = this.getAppPublicOrigin();
      enriched['hasDefinedTerm'] = enriched['hasDefinedTerm'].map((term) => {
        if (!term || typeof term !== 'object') {
          return term;
        }
        const row = term as Record<string, unknown>;
        const url = row['url'];
        if (typeof url === 'string' && url.startsWith('/')) {
          return { ...row, url: `${origin}${url}` };
        }
        return row;
      });
    }

    return enriched;
  }

  private setCanonicalUrl(href: string): void {
    const head = this.document.head;
    let link = this.document.getElementById(CANONICAL_LINK_ID) as HTMLLinkElement | null;
    if (!link) {
      link = this.document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    }
    if (!link) {
      link = this.document.createElement('link');
      head.appendChild(link);
    }
    link.id = CANONICAL_LINK_ID;
    link.setAttribute('rel', 'canonical');
    link.setAttribute('href', href);
  }

  private applyArticleAndAuthorMeta(page: PageSeo): void {
    this.meta.removeTag('property="article:author"');
    this.meta.removeTag('property="article:published_time"');
    this.meta.removeTag('name="author"');

    const author = page.articleAuthor?.trim();
    if (author) {
      const published = (page.articlePublishedTime?.trim() || new Date().toISOString());
      this.meta.updateTag({ property: 'article:author', content: author });
      this.meta.updateTag({ property: 'article:published_time', content: published });
      this.meta.updateTag({ name: 'author', content: author });
    }
  }
}
